import { useState } from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { PencilIcon } from '@heroicons/react/24/outline';
import ExpandableCard from '../../../components/shared/ExpandableCard';
import ConsequenceTypeExpandedContent from './ConsequenceTypeExpandedContent';
import { getIconByName } from '../../../utils/iconMapping';

const FALLBACK_ICONS = {
  'demerit': 'ExclamationTriangleIcon',
  'warning': 'ExclamationTriangleIcon',
  'detention': 'ClockIcon',
  'homework-detention': 'BookOpenIcon',
  'friday-detention': 'CalendarDaysIcon',
  'saturday-detention': 'CalendarIcon',
  'suspension': 'NoSymbolIcon',
  'privilege-loss': 'LockClosedIcon',
  'referral': 'DocumentTextIcon',
  'contract': 'DocumentIcon',
  'restorative': 'HandRaisedIcon'
};

export default function SortableExpandableConsequenceCard({ type, onEditClick, onTypeUpdated, embedded = false }) {
  const [expanded, setExpanded] = useState(false);

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: type.id });

  const sortableStyle = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  // Auto-collapse when dragging
  const isExpanded = isDragging ? false : expanded;

  const getIcon = () => {
    if (type.icon && type.icon.endsWith('Icon')) {
      return getIconByName(type.icon, 'w-5 h-5');
    }
    const fallbackIconName = FALLBACK_ICONS[type.value] || 'ExclamationTriangleIcon';
    return getIconByName(fallbackIconName, 'w-5 h-5');
  };

  const header = (
    <div className="flex items-center gap-2">
      {/* Drag Handle */}
      <div
        className="cursor-grab hover:bg-base-content/5 rounded p-1 -ml-1 flex-shrink-0"
        {...attributes}
        {...listeners}
        onClick={(e) => e.stopPropagation()}
      >
        <svg className="w-4 h-4 opacity-40" fill="currentColor" viewBox="0 0 20 20">
          <circle cx="7" cy="5" r="1.5" />
          <circle cx="7" cy="10" r="1.5" />
          <circle cx="7" cy="15" r="1.5" />
          <circle cx="13" cy="5" r="1.5" />
          <circle cx="13" cy="10" r="1.5" />
          <circle cx="13" cy="15" r="1.5" />
        </svg>
      </div>

      {/* Icon */}
      <div className="flex-shrink-0">{getIcon()}</div>

      {/* Label & badges */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="font-medium truncate">{type.label}</span>
          {type.active === false && (
            <span className="badge badge-ghost badge-sm">Inactive</span>
          )}
        </div>
        <div className="flex items-center gap-2 mt-1">
          <code className="text-xs bg-base-200 px-1.5 py-0.5 rounded">{type.value}</code>
          {!embedded && type.severity && (
            <span className={`badge badge-${
              type.severity === 'low' ? 'info' :
              type.severity === 'high' ? 'error' :
              'warning'
            } badge-xs`}>
              {type.severity}
            </span>
          )}
          {type.requiresAction && (
            <span className="badge badge-success badge-xs">Requires Action</span>
          )}
        </div>
        {type.description && (
          <p className="text-sm opacity-70 mt-1 line-clamp-2">{type.description}</p>
        )}
      </div>
    </div>
  );

  const actions = (
    <button
      className="btn btn-ghost btn-xs btn-circle"
      onClick={(e) => {
        e.stopPropagation();
        onEditClick(type);
      }}
      title="Edit type"
    >
      <PencilIcon className="w-4 h-4 opacity-40" />
    </button>
  );

  return (
    <div ref={setNodeRef} style={sortableStyle}>
      <ExpandableCard
        expanded={isExpanded}
        onToggle={() => setExpanded(!expanded)}
        header={header}
        actions={actions}
        flat={embedded}
        className={`${type.active === false ? 'opacity-50' : ''}`}
        style={{ borderLeftWidth: '6px', borderLeftColor: type.color || '#06B6D4' }}
      >
        <ConsequenceTypeExpandedContent type={type} onTypeUpdated={onTypeUpdated} />
      </ExpandableCard>
    </div>
  );
}
